import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export type OutputFormat = 'docx' | 'pdf' | 'gdoc' | 'json';

export interface EnhanceResult {
  enhancedText: string;
  outputFormat: OutputFormat;

  // Where the generated file lives (if any)
  downloadUrl?: string;
  gcsPath?: string;
  publicUrl?: string;
  googleDocId?: string;
  googleDocUrl?: string;

  // Optional error from file creation/upload
  fileError?: string;
}

export interface EnhanceJsonResult {
  model: string;
  resumeJson: any | null;
  rawModelJson: string;
}

@Injectable({ providedIn: 'root' })
export class ResumeEnhancerService {
  /**
   * Cloud Function endpoint – must match your deployed URL.
   */
  private readonly endpoint =
    ((environment as any).videoJobsBaseUrl ||
      'https://us-central1-clarichatai.cloudfunctions.net/enhanceResume').replace(/\/+$/, '');

  constructor(private http: HttpClient) {}

  /**
   * Classic enhancement call – returns enhanced text
   * + (optionally) a generated file (docx/pdf/gdoc).
   *
   * Sends ONLY JSON to the Cloud Function, including resumeText and (optionally) resumeUrl.
   */
  enhanceResume(params: {
    jobTitle: string;
    jobDescription: string;
    resumeText: string;        // may be empty; backend can fall back to resumeUrl
    resumeUrl?: string;        // Firebase Storage URL
    outputFormat: OutputFormat; // 'docx' | 'pdf' | 'gdoc' | 'json'
    jobLabel?: string;         // e.g. "Senior / Lead"
    resumeTitle?: string;
    candidateHistory?: string;
  }): Observable<EnhanceResult> {
    const body: any = {
      jobTitle: params.jobTitle,
      jobDescription: params.jobDescription,
      jobLabel: params.jobLabel,
      resumeTitle: params.resumeTitle,
      candidateHistory: params.candidateHistory,
      resumeText: params.resumeText,
      outputFormat: params.outputFormat
    };

    if (params.resumeUrl) {
      body.resumeUrl = params.resumeUrl;
    }

    return this.http.post<any>(this.endpoint, body).pipe(
      map((res) => {
        const outFormat = (res.outputFormat || params.outputFormat) as OutputFormat;
        const downloadUrl: string | null =
          res.publicUrl || res.googleDocUrl || null;

        const result: EnhanceResult = {
          enhancedText: res.enhancedText,
          outputFormat: outFormat,
          downloadUrl: downloadUrl || undefined,
          gcsPath: res.gcsPath,
          publicUrl: res.publicUrl,
          googleDocId: res.googleDocId,
          googleDocUrl: res.googleDocUrl,
          fileError: res.fileError
        };

        return result;
      })
    );
  }

  /**
   * JSON-only enhancement call – asks the same Cloud Function to
   * return structured resume JSON (outputFormat: "json").
   */
  enhanceResumeJson(params: {
    jobTitle: string;
    jobDescription: string;
    resumeText: string;        // may be empty; backend can fall back to resumeUrl
    resumeUrl?: string;        // Firebase Storage URL
    jobRoleLabel: string;      // mapped to jobLabel on backend
    resumeTitle?: string;
    candidateHistory?: string;
  }): Observable<EnhanceJsonResult> {
    const body: any = {
      jobTitle: params.jobTitle,
      jobDescription: params.jobDescription,
      jobLabel: params.jobRoleLabel,
      resumeTitle: params.resumeTitle,
      candidateHistory: params.candidateHistory,
      resumeText: params.resumeText,
      outputFormat: 'json'
    };

    if (params.resumeUrl) {
      body.resumeUrl = params.resumeUrl;
    }

    return this.http.post<EnhanceJsonResult>(this.endpoint, body);
  }
}
